/**
 * @mentions in comment bodies (spec 137, service spec 122).
 *
 * A mention is stored as an ordinary Markdown link, `[@Sam Lee](#mention-<id>)`,
 * so anything that does not know about mentions still reads "@Sam Lee". While
 * a comment is being written it shows only `@Sam Lee`; the people chosen from
 * the picker are kept beside the text, and only a chosen name still present
 * in the text becomes a mention when it is sent.
 */

import { MENTION_HREF_PREFIX } from '@/shared/components/mentionHref';

/** The service's pattern (service spec 122 plan), kept in step with it. */
const MENTION = /\[@([^\]\n]{1,200})\]\(#mention-([0-9a-fA-F-]{36})\)/g;

export { MENTION_HREF_PREFIX };

export interface MentionedPerson {
  id: string;
  name: string;
}

/** The body as people read it: each mention as `@Name`. */
export function toPlain(body: string): string {
  return body.replace(MENTION, (_match, name: string) => `@${name}`);
}

/**
 * The body to send: every chosen person whose `@Name` is still in the text
 * becomes a mention. Longer names first, so "@Sam Lee" is not taken for a
 * chosen "@Sam".
 */
export function serialize(text: string, chosen: MentionedPerson[]): string {
  const people = [...new Map(chosen.map((p) => [p.name, p])).values()].sort(
    (a, b) => b.name.length - a.name.length,
  );
  let out = text;
  for (const person of people) {
    out = out.replace(
      new RegExp(`(^|[^\\w[])@${escapeRegExp(person.name)}(?![\\w])`, 'g'),
      (_match, before: string) =>
        `${before}[@${person.name}](${MENTION_HREF_PREFIX}${person.id})`,
    );
  }
  return out;
}

/**
 * What is being typed after an `@` at the caret, or `null` when the caret is
 * not in a mention: the `@` starts the text or follows whitespace, and what
 * follows it has no line break and is short.
 */
export function activeQuery(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at < 0) return null;
  if (at > 0 && !/\s/.test(before[at - 1] ?? '')) return null;
  const query = before.slice(at + 1);
  if (query.length > 40 || /[\n@]/.test(query)) return null;
  return { start: at, query };
}

/** The text with the query at `start` replaced by `@Name `, and where the caret goes. */
export function insertMention(
  text: string,
  start: number,
  caret: number,
  name: string,
): { text: string; caret: number } {
  const inserted = `@${name} `;
  return {
    text: text.slice(0, start) + inserted + text.slice(caret),
    caret: start + inserted.length,
  };
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
